// Centralized Firebase Realtime Database status subscription service.
//
// Why this exists
// ---------------
// The realtime device status nodes (e.g. `status/{device}/{mac}/general`) are
// read by several unrelated features at once: the active-alarms store, the
// dashboard cards, the weight widgets and the bird-weight detail view. When each
// of them attached and detached its own Firebase listener on the same location,
// a single `ref.off()` (which removes *every* listener at that path) could tear
// down another feature's monitoring — this is exactly what broke realtime alarm
// updates in IND-1845.
//
// This service is the single owner of the underlying Firebase listeners. Every
// consumer subscribes through `subscribeStatus(path, callback)` and receives an
// `unsubscribe` handle that removes only its own callback. The real Firebase
// listener for a path is attached once (on the first subscriber) and detached
// only when the last subscriber leaves. No consumer ever touches the raw ref, so
// it is impossible for one feature to cancel another feature's monitoring.
import firebase from 'firebase/app'
import 'firebase/database'
import { DEMO_MODE } from '../demo/demo-mode'
import { subscribeStatusDemo } from '../demo/mock-realtime'

// path -> { ref, dbCallback, subscribers:Set<fn>, hasValue:boolean, lastValue }
const registry = new Map()

// Subscribes to a realtime status path. `callback` is invoked with the node's
// value (snapshot.val()) on every change, and once immediately with the last
// known value when it is already cached. Returns an idempotent unsubscribe
// function that removes only this subscription.
export function subscribeStatus (path, callback) {
  // Demo build: emit curated, animated live data instead of touching Firebase.
  if (DEMO_MODE) {
    return subscribeStatusDemo(path, callback)
  }
  if (!path || typeof callback !== 'function') {
    return () => {}
  }

  let entry = registry.get(path)
  if (!entry) {
    const ref = firebase.database().ref(path)
    entry = { ref, dbCallback: null, subscribers: new Set(), hasValue: false, lastValue: undefined }
    entry.dbCallback = ref.on('value', snap => {
      entry.hasValue = true
      entry.lastValue = snap.val()
      entry.subscribers.forEach(subscriber => {
        try {
          subscriber(entry.lastValue)
        } catch (err) {
          // A misbehaving subscriber must never break the fan-out to the others.
        }
      })
    }, () => {})
    registry.set(path, entry)
  }

  entry.subscribers.add(callback)

  // Deliver the cached value immediately so a late subscriber renders without
  // waiting for the next realtime tick.
  if (entry.hasValue) {
    try {
      callback(entry.lastValue)
    } catch (err) {
      // ignore subscriber errors on the initial delivery
    }
  }

  let active = true
  return function unsubscribe () {
    if (!active) {
      return
    }
    active = false
    const current = registry.get(path)
    if (!current) {
      return
    }
    current.subscribers.delete(callback)
    if (current.subscribers.size === 0) {
      current.ref.off('value', current.dbCallback)
      registry.delete(path)
    }
  }
}

// Builds a canonical status path for a device node/mac, mirroring how the app
// resolves the realtime location (`status/{base||type}/{mac}[/child]`).
export function deviceStatusPath (node, mac, child) {
  const base = `status/${node}/${mac}`
  return child ? `${base}/${child}` : base
}



// WEBPACK FOOTER //
// ./src/services/realtime-status.js